import { Trans, t } from '@lingui/macro';
import { useEffect, useReducer, useState } from 'react';
import { useHistory } from '../../hooks/useRouterCompat';
import { Folder, Trash2, Loader2 } from 'lucide-react';

import { ExternalShareLink, ExternalShareLinkDownload } from '../../models/ExternalShareLink';
import { Room } from '../../models/Room';
import { RoomItem } from '../../models/RoomItem';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { ShareLinkModal } from '../components/ShareLinkModal';

import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { cn } from "../../lib/utils";

const ExternalFileSharePage: React.FC<{ room: Room, submodal: (v: boolean) => void }> = ({ room, submodal }) => {
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [links, setLinks] = useState<ExternalShareLink[]>([]);
  const [downloads, setDownloads] = useState<ExternalShareLinkDownload[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<ExternalShareLink>();
  const [items, setItems] = useState<RoomItem[]>([]);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  // 삭제 다이얼로그
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<ExternalShareLink | null>(null);

  const roomName = room ? Room.getRoomNameWithSharp(room, My.user.id) : '';

  useEffect(() => {
    setLinks([]);
    const unsub1 = Fire.item.subscribeToRoomItemsByType(room.id, ['file'], items => {
      setItems(items);
    });

    const unsub2 = Fire.item.subscribeToExternalShareLinks(room.id, (links) => {
      setLinks(links);
    });

    return () => {
      unsub1();
      unsub2();
    }
  }, []);

  useEffect(() => {
    if (selectedDraft) {
      submodal(true);
      return Fire.item.subscribeToExternalShareDownloads(selectedDraft, downs => {
        setDownloads(downs);
      })
    }
    else {
      submodal(false);
    }
  }, [selectedDraft])

  function changeSelectedLink(link: ExternalShareLink | undefined) {
    setSelectedDraft(link ? Util.pojo(link) : undefined);
  }

  function handleDeleteClick(link: ExternalShareLink) {
    setLinkToDelete(link);
    setShowDeleteDialog(true);
  }

  async function confirmDelete() {
    if (linkToDelete) {
      await Fire.item.deleteExternalShareLink(linkToDelete);
      changeSelectedLink(undefined);
    }
    setShowDeleteDialog(false);
    setLinkToDelete(null);
  }

  function getLinkTitle(link: ExternalShareLink): string {
    if (link.title) return link.title;
    if (link.roomItems.length > 0) {
      return Util.getTruncatedFileName(link.roomItems[0].title, 100) +
        (link.roomItems.length > 1 ? t` and ${link.roomItems.length - 1} other files` : '');
    }
    return t`No files added`;
  }

  return (
    <div className="flex flex-col h-full bg-white md:px-2">
      {/* 헤더 영역 */}
      <div className="flex items-center gap-2 p-3 border-b border-gray-100">
        <Folder className="w-5 h-5 text-orange-500" />
        <span className="font-bold text-sm text-gray-900">
          {Room.getRoomNameWithSharp(room, My.user.id)}
        </span>
      </div>

      {/* 로딩 인디케이터 */}
      {inProgress && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
        </div>
      )}

      {/* 테이블 헤더 */}
      <div className="flex items-center px-4 py-3 bg-white border-b border-gray-100 text-xs font-bold text-gray-900">
        <div className="flex-1 min-w-0"><Trans>Title</Trans></div>
        <div className="hidden sm:flex w-[450px] shrink-0 items-center justify-end gap-2">
          <div className="w-[130px] text-center"><Trans>Created Date</Trans></div>
          <div className="w-[150px] text-left pl-2"><Trans>Creator</Trans></div>
          <div className="w-[130px] text-center"><Trans>Expire Date</Trans></div>
          <div className="w-[40px]"></div>
        </div>
      </div>

      {/* 링크 목록 */}
      <div className="flex-1 overflow-y-auto bg-white">
        {links.length === 0 && !inProgress && (
          <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
            <Trans>No links in this room.</Trans>
          </div>
        )}

        {links.map(link => (
          <div
            key={link.id}
            className={cn(
              "flex items-center px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer",
              selectedDraft?.id === link.id && "bg-orange-50"
            )}
            onClick={() => changeSelectedLink(link)}
          >
            {/* 제목 영역 */}
            <div className="flex-1 min-w-0 pr-4">
              <p className="text-sm font-medium text-gray-900 truncate hover:text-orange-600 transition-colors">
                {getLinkTitle(link)}
              </p>
              {/* 모바일에서만 보이는 정보 (sm 미만일 때) */}
              <div className="sm:hidden flex items-center gap-2 mt-1 text-xs text-gray-500">
                <span>{UtilApp.dateStrToTimeAndDay(link.updated, My.user.lang)}</span>
                <span>•</span>
                <span>{link.user.name}</span>
              </div>
            </div>

            {/* 고정 너비 정보 영역 (sm 이상일 때 보임) */}
            <div className="hidden sm:flex w-[450px] shrink-0 items-center justify-end gap-2">
              <div className="w-[130px] text-center text-xs text-gray-500">
                {UtilApp.dateStrToTimeAndDay(link.updated, My.user.lang)}
              </div>

              <div className="w-[150px] flex items-center gap-2 pl-2">
                <Avatar className="w-6 h-6 shrink-0">
                  <AvatarImage src={link.user.photoUrl ?? Util.ANONYMOUS_PHOTO_URL} />
                  <AvatarFallback className="text-[10px]">
                    {link.user.name?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-gray-900 truncate">{link.user.name}</span>
              </div>

              <div className="w-[130px] text-center text-xs text-gray-500">
                {link.expireDate ? UtilApp.dateStrToTimeAndDay(link.expireDate, My.user.lang) : '-'}
              </div>

              <div className="w-[40px] flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(link);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* 모바일 삭제 버튼 (sm 미만일 때) */}
            <div className="sm:hidden pl-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(link);
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Delete link`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Are you sure you want to delete this link?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              {t`Delete`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ShareLinkModal */}
      {selectedDraft && room && (
        <ShareLinkModal
          show={true}
          isNew={false}
          setShow={() => setSelectedDraft(undefined)}
          link={selectedDraft}
          room={room}
          downloads={downloads}
          items={items}
          onDone={() => { }}
        />
      )}
    </div>
  );
}

export default ExternalFileSharePage;
